import { useState, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { auth } from "../lib/auth";
import { api, fmtDate, fmtDateOnly } from "../lib/api";
import { toast } from "sonner";
import { Bookmark, Copy, Download, ExternalLink, Search, Trash2, Upload, Lock, Unlock, Eye, HardDriveDownload } from "lucide-react";
import HowItWorks from "../components/HowItWorks";
import { saveExecToDevice } from "../components/SummaryDiagramResults";

const LAYERS = [
  { v: "all", l: "ALL", color: "border-zinc-500" },
  { v: "topic", l: "TOPIC", color: "border-emerald-400" },
  { v: "innovation", l: "INNOVATION", color: "border-purple-400" },
  { v: "bot", l: "BOT_OUTPUT", color: "border-amber-400" },
  { v: "custom", l: "CUSTOM", color: "border-rose-400" },
  { v: "onestamp", l: "ONESTAMP", color: "border-sky-400" },
];

export default function MyLibraryPage() {
  const qc = useQueryClient();
  const [layer, setLayer] = useState("all");
  const [q, setQ] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [viewingId, setViewingId] = useState(null);
  const { data: items = [] } = useQuery({
    queryKey: ["saves", layer],
    queryFn: () => auth.get(`/saves?layer=${layer === "onestamp" ? "all" : layer}`).then((r) => r.data),
  });
  const { data: stampData } = useQuery({
    queryKey: ["onestamp-list"],
    queryFn: () => api.get("/onestamp/list").then((r) => r.data),
  });
  const { data: stats } = useQuery({
    queryKey: ["saves-stats"],
    queryFn: () => auth.get("/saves/stats").then((r) => r.data),
  });
  const del = useMutation({
    mutationFn: (id) => auth.delete(`/saves/${id}`).then((r) => r.data),
    onSuccess: () => {
      toast.success("REMOVED_FROM_LIBRARY");
      qc.invalidateQueries({ queryKey: ["saves"] });
      qc.invalidateQueries({ queryKey: ["saves-stats"] });
    },
    onError: (e) => {
      const msg = e?.response?.data?.detail || e?.message || "unknown error";
      toast.error("DELETE_FAILED", { description: msg });
    },
  });

  const filtered = items.filter((i) => {
    if (q.trim()) {
      const hay = (
        (i.title || "") + " " + (i.text || "") + " " +
        (i.layer || "") + " " + ((i.tags || []).join(" "))
      ).toLowerCase();
      if (!hay.includes(q.toLowerCase())) return false;
    }
    if (dateFilter) {
      const created = (i.created_at || "").slice(0, 10);
      if (created !== dateFilter) return false;
    }
    return true;
  });

  const fileInputRef = useRef(null);

  const downloadAll = () => {
    const payload = {
      exported_at: new Date().toISOString(),
      count: items.length,
      items,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)],
      { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `mindstein-library-${new Date().toISOString().slice(0,10)}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("LIBRARY_DOWNLOADED", { description: `${items.length} items` });
  };

  const downloadZip = async () => {
    try {
      toast.info("PACKING_ZIP…");
      const r = await api.get("/library/export", { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `mindstein-library-${new Date().toISOString().slice(0,10)}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("ZIP_DOWNLOADED", { description: "Markdown + files + manifest" });
    } catch (e) {
      toast.error("ZIP_FAILED", { description: e?.response?.data?.detail || e?.message });
    }
  };

  const onUploadFile = async (evt) => {
    const file = evt.target.files?.[0];
    evt.target.value = ""; // reset so same file can be re-uploaded
    if (!file) return;
    if (file.size > 512 * 1024) {
      toast.error("FILE_TOO_LARGE", { description: "Max 512 KB. Split larger docs." });
      return;
    }
    try {
      const text = await file.text();
      // Detect JSON export → bulk import
      let importedCount = 0;
      try {
        const parsed = JSON.parse(text);
        if (Array.isArray(parsed?.items)) {
          for (const it of parsed.items.slice(0, 200)) {
            await api.post("/saves", {
              layer: "custom",
              title: it.title || file.name,
              text: it.text || "",
              payload: it.payload || {},
              note: it.note || `imported from ${file.name}`,
            });
            importedCount += 1;
          }
          toast.success("BULK_IMPORTED", { description: `${importedCount} items` });
          qc.invalidateQueries({ queryKey: ["saves"] });
          qc.invalidateQueries({ queryKey: ["saves-stats"] });
          return;
        }
      } catch (_) { /* not JSON — treat as plain text */ }
      // Plain text / markdown → single custom save
      await api.post("/saves", {
        layer: "custom",
        title: file.name,
        text: text.slice(0, 8000),
        payload: { source: "user_upload", size: file.size, type: file.type },
        note: "uploaded to MindStein",
      });
      toast.success("UPLOADED_TO_MINDSTEIN", { description: file.name });
      qc.invalidateQueries({ queryKey: ["saves"] });
      qc.invalidateQueries({ queryKey: ["saves-stats"] });
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "unknown error";
      toast.error("UPLOAD_FAILED", { description: msg });
    }
  };

  return (
    <div data-testid="my-library-page" className="p-4 space-y-4">
      <header>
        <div className="text-[10px] uppercase tracking-[0.2em] text-purple-300 font-mono inline-flex items-center gap-2">
          <Bookmark size={12} /> RESEARCH_LIBRARY
        </div>
        <h1 className="font-mono text-2xl tracking-tight mt-1">Research Library</h1>
        <p className="text-[12px] text-zinc-500 mt-1 max-w-2xl">
          Search all your previous synthesis outputs, saved queries, and research
          history by keyword, topic, domain, or date. Everything lives forever —
          survives 30-day topic expiry and innovation rotation.
        </p>
      </header>

      <HowItWorks
        id="library"
        tag="PERSISTENT · YOURS · NEVER EXPIRES"
        headline="Everything you've saved across the app lives here. Items keep a payload copy — so they stay even after the original idea / innovation / fact expires."
        steps={[
          { accent: "purple", label: "Pick a layer", body: "Switch between IDEAS · INNOVATION · FACTS · BOT · ALL with the tabs below — counts show how many you've saved per layer." },
          { accent: "amber", label: "Search instantly", body: "Type any keyword in the search bar — matches across titles, summaries, and your custom notes." },
          { accent: "emerald", label: "Copy or jump", body: "COPY pastes the item to your clipboard; the external-link icon opens the source URL in a new tab." },
          { accent: "rose", label: "Delete to remove", body: "DELETE clears the save permanently. Items only disappear when you choose — never auto-expire." },
          { accent: "sky", label: "Remix in the Bot", body: "Open BOT and attach saved items as references to combine, compare, contrast or remix into new outputs." },
        ]}
      />

      <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
        {LAYERS.map((L) => (
          <button
            key={L.v}
            data-testid={`library-tab-${L.v}`}
            onClick={() => setLayer(L.v)}
            className={
              "border bg-[#0E0E0E] p-3 text-left hover:bg-[#141414] " +
              (layer === L.v ? L.color : "border-[#27272A]")
            }
          >
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">
              {L.l}
            </div>
            <div className="text-2xl font-mono tabnum text-white">
              {L.v === "all" ? stats?.total ?? 0
                : L.v === "onestamp" ? stampData?.total ?? 0
                : stats?.[L.v] ?? 0}
            </div>
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2 border-y border-[#27272A] px-3 py-2 bg-[#0E0E0E]">
        <Search size={12} className="text-zinc-500" />
        <input
          data-testid="library-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="SEARCH_KEYWORD_TOPIC_DOMAIN"
          className="bg-transparent outline-none text-[12px] font-mono text-white placeholder:text-zinc-600 w-64"
        />
        <input
          data-testid="library-date-filter"
          type="date"
          value={dateFilter}
          onChange={(e) => setDateFilter(e.target.value)}
          title="Filter by date"
          className="bg-[#141414] border border-[#27272A] outline-none text-[11px] font-mono text-white px-2 h-7"
        />
        {(dateFilter || q) && (
          <button
            data-testid="library-clear-filters"
            onClick={() => { setQ(""); setDateFilter(""); }}
            className="text-[10px] font-mono text-zinc-500 hover:text-white"
          >
            CLEAR
          </button>
        )}
        <span className="ml-auto text-[10px] font-mono text-zinc-500 tabnum">
          {filtered.length} / {items.length}
        </span>
        <input
          ref={fileInputRef}
          type="file"
          accept=".json,.txt,.md,.csv,text/plain"
          onChange={onUploadFile}
          className="hidden"
          data-testid="library-file-input"
        />
        <button
          data-testid="library-upload-btn"
          onClick={() => fileInputRef.current?.click()}
          title="Upload a .json / .txt / .md file into your library"
          className="inline-flex items-center gap-1 px-2 h-7 text-[10px] font-mono uppercase tracking-wider border border-emerald-400/60 text-emerald-300 hover:bg-emerald-500/10"
        >
          <Upload size={11} /> UPLOAD
        </button>
        <button
          data-testid="library-download-btn"
          onClick={downloadAll}
          disabled={items.length === 0}
          title="Download your entire library as JSON"
          className="inline-flex items-center gap-1 px-2 h-7 text-[10px] font-mono uppercase tracking-wider border border-amber-400/60 text-amber-300 hover:bg-amber-500/10 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Download size={11} /> DOWNLOAD_JSON
        </button>
        <button
          data-testid="library-download-zip-btn"
          onClick={downloadZip}
          disabled={items.length === 0}
          title="Download your entire library as a ZIP (markdown + original files + manifest)"
          className="inline-flex items-center gap-1 px-2 h-7 text-[10px] font-mono uppercase tracking-wider border border-emerald-400/60 text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Download size={11} /> DOWNLOAD_ZIP
        </button>
      </div>

      {layer === "onestamp" ? (
        <OneStampMiniList items={stampData?.items || []} q={q} />
      ) : (
      <section className="border border-[#27272A]">
        {filtered.length === 0 ? (
          <div className="grid-bg p-10 text-center text-[12px] font-mono text-zinc-500 uppercase tracking-wider">
            NO_SAVED_ITEMS · go SAVE one from a TOPIC or the INNOVATION feed
          </div>
        ) : (
          filtered.map((it) => (
            <div
              key={it.id}
              data-testid={`saved-item-${it.id}`}
              className="border-b border-[#161616] p-3 hover:bg-[#0E0E0E] grid grid-cols-12 gap-3"
            >
              <div className="col-span-2 font-mono text-[11px] uppercase tracking-wider">
                <span
                  className="dot"
                  style={{
                    background:
                      it.layer === "topic" ? "#10B981"
                      : it.layer === "innovation" ? "#A855F7"
                      : it.layer === "bot" ? "#F59E0B" : "#F43F5E",
                  }}
                />
                {it.layer}
                <div className="text-[10px] text-zinc-500 mt-1">
                  {it.topic_slug || ""}
                </div>
                <div className="text-[10px] text-zinc-600 mt-1">
                  {fmtDateOnly(it.created_at)}
                </div>
              </div>
              <div className="col-span-9 min-w-0">
                {it.title && (
                  <div className="text-[14px] text-white flex items-center gap-2">
                    {it.title}
                    {it.pin_locked && (
                      <span
                        data-testid={`saved-locked-${it.id}`}
                        className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider text-amber-300 border border-amber-400/60 px-1.5 py-0.5"
                      >
                        <Lock size={9} /> PIN_LOCKED
                      </span>
                    )}
                  </div>
                )}
                {it.locked ? (
                  <div className="text-[11px] font-mono text-amber-300/80 mt-1">
                    Content is PIN-locked. Unlock via /security to view.
                  </div>
                ) : (
                  it.text && (
                    <div className="text-[12px] text-zinc-400 whitespace-pre-wrap leading-snug mt-1 line-clamp-4">
                      {it.text}
                    </div>
                  )
                )}
                {it.note && !it.locked && (
                  <div className="text-[11px] text-amber-300 font-mono mt-1">
                    ↳ {it.note}
                  </div>
                )}
                {!it.locked && it.payload?.source_1 && (
                  <div className="flex gap-3 mt-2 text-[11px] font-mono text-zinc-400">
                    <a
                      href={it.payload.source_1}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-white inline-flex items-center gap-1"
                    >
                      S1 <ExternalLink size={10} />
                    </a>
                    {it.payload.source_2 && (
                      <a
                        href={it.payload.source_2}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-white inline-flex items-center gap-1"
                      >
                        S2 <ExternalLink size={10} />
                      </a>
                    )}
                    {it.payload.source_3 && (
                      <a
                        href={it.payload.source_3}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-white inline-flex items-center gap-1"
                      >
                        S3 <ExternalLink size={10} />
                      </a>
                    )}
                  </div>
                )}
              </div>
              <div className="col-span-1 flex flex-col gap-1 items-end">
                <button
                  data-testid={`view-save-${it.id}`}
                  onClick={() => setViewingId(it.id)}
                  title="Open full view"
                  className="inline-flex items-center gap-1 px-2 h-6 text-[10px] font-mono uppercase tracking-wider border border-[#27272A] text-zinc-300 hover:border-emerald-400 hover:text-white"
                >
                  <Eye size={10} /> VIEW
                </button>
                <button
                  data-testid={`pin-toggle-${it.id}`}
                  onClick={async () => {
                    try {
                      await api.post(`/saves/${it.id}/${it.pin_locked ? "pin-unlock" : "pin-lock"}`);
                      toast.success(it.pin_locked ? "PIN_UNLOCKED" : "PIN_LOCKED");
                      qc.invalidateQueries({ queryKey: ["saves"] });
                    } catch (e) {
                      toast.error("PIN_TOGGLE_FAIL", { description: e?.response?.data?.detail || e?.message });
                    }
                  }}
                  title={it.pin_locked ? "Remove PIN lock" : "PIN-lock this save"}
                  className={`inline-flex items-center gap-1 px-2 h-6 text-[10px] font-mono uppercase tracking-wider border ${
                    it.pin_locked
                      ? "border-amber-400 text-amber-300 bg-amber-500/10"
                      : "border-[#27272A] text-zinc-300 hover:border-amber-400 hover:text-white"
                  }`}
                >
                  {it.pin_locked ? <><Unlock size={10} /> UNLOCK</> : <><Lock size={10} /> PIN</>}
                </button>
                {!it.locked && it.payload?.exec_summary && (
                  <button
                    data-testid={`save-device-${it.id}`}
                    onClick={() => saveExecToDevice(it.payload.exec_summary, (it.ref_id || it.id).slice(0, 8))}
                    title="Save to Device — downloads the Executive Summary PDF + diagram PNG"
                    className="inline-flex items-center gap-1 px-2 h-6 text-[10px] font-mono uppercase tracking-wider border border-sky-400/60 text-sky-300 hover:bg-sky-500/10"
                  >
                    <HardDriveDownload size={10} /> DEVICE
                  </button>
                )}
                <button
                  data-testid={`copy-save-${it.id}`}
                  onClick={() => {
                    navigator.clipboard?.writeText(
                      it.title + (it.text ? "\n" + it.text : ""),
                    );
                    toast.success("COPIED");
                  }}
                  title="Copy to clipboard"
                  className="inline-flex items-center gap-1 px-2 h-6 text-[10px] font-mono uppercase tracking-wider border border-[#27272A] text-zinc-300 hover:border-amber-400 hover:text-white"
                >
                  <Copy size={10} /> COPY
                </button>
                <button
                  data-testid={`delete-save-${it.id}`}
                  onClick={() => {
                    if (window.confirm(`Delete "${it.title || (it.text || "").slice(0,40)}" from your library?`)) {
                      del.mutate(it.id);
                    }
                  }}
                  disabled={del.isPending}
                  title="Delete from My Library"
                  className="inline-flex items-center gap-1 px-2 h-6 text-[10px] font-mono uppercase tracking-wider border border-rose-400/60 text-rose-300 hover:bg-rose-500/10 disabled:opacity-50"
                >
                  <Trash2 size={10} /> {del.isPending && del.variables === it.id ? "…" : "DELETE"}
                </button>
              </div>
            </div>
          ))
        )}
      </section>
      )}
      {viewingId && (
        <SaveViewDrawer
          saveId={viewingId}
          onClose={() => setViewingId(null)}
          onDelete={() => { del.mutate(viewingId); setViewingId(null); }}
        />
      )}
    </div>
  );
}

function OneStampMiniList({ items, q }) {
  const API = process.env.REACT_APP_BACKEND_URL;
  const shown = items.filter((s) =>
    !q.trim() ? true :
      ((s.preview || "") + " " + (s.kind || "")).toLowerCase().includes(q.toLowerCase())
  );
  const verify = async (id) => {
    try {
      const r = await api.get(`/onestamp/${id}/verify`);
      r.data.valid
        ? toast.success("VERIFIED · PROOF_INTACT", { description: `hash ${r.data.content_hash?.slice(0, 16)}…` })
        : toast.error("VERIFICATION_FAILED", { description: "Content or signature mismatch" });
    } catch (e) {
      toast.error("VERIFY_ERROR", { description: e?.response?.data?.detail || e?.message });
    }
  };
  return (
    <section data-testid="library-onestamp-list" className="border border-sky-400/40">
      <div className="flex items-center justify-between px-3 py-2 border-b border-[#27272A] bg-[#0E0E0E]">
        <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-sky-300">
          ONESTAMP · PRIOR-ART PROOFS · AUTO-STAMPED ON EVERY BOT ANSWER
        </span>
        <a
          href="/onestamp"
          data-testid="library-open-onestamp-page"
          className="text-[10px] font-mono uppercase tracking-wider text-sky-300 border border-sky-400/60 px-2 py-1 hover:bg-sky-500/10"
        >
          OPEN_FULL_ONESTAMP_PAGE →
        </a>
      </div>
      {shown.length === 0 ? (
        <div className="grid-bg p-10 text-center text-[12px] font-mono text-zinc-500 uppercase tracking-wider">
          NO_STAMPS_YET · ask the BOT anything — every answer is automatically
          hashed &amp; timestamped as prior-art proof
        </div>
      ) : (
        shown.map((s) => (
          <div
            key={s.stamp_id}
            data-testid={`stamp-row-${s.stamp_id}`}
            className="border-b border-[#161616] p-3 hover:bg-[#0E0E0E] flex items-start gap-3"
          >
            <div className="min-w-0 flex-1">
              <div className="text-[12px] text-white line-clamp-2">{s.preview || s.stamp_id}</div>
              <div className="text-[10px] font-mono text-zinc-500 uppercase mt-1">
                {s.kind} · {fmtDate(s.created_at)} ·{" "}
                <span className="text-sky-300">{s.algo}</span>
                {s.share_code && <span className="text-emerald-300"> · SHARED</span>}
              </div>
            </div>
            <div className="flex gap-1 shrink-0">
              <button
                data-testid={`stamp-verify-${s.stamp_id}`}
                onClick={() => verify(s.stamp_id)}
                className="px-2 h-6 text-[10px] font-mono uppercase tracking-wider border border-emerald-400/60 text-emerald-300 hover:bg-emerald-500/10"
              >
                VERIFY
              </button>
              <button
                data-testid={`stamp-pdf-${s.stamp_id}`}
                onClick={() => window.open(`${API}/api/onestamp/${s.stamp_id}/pdf`, "_blank")}
                className="px-2 h-6 text-[10px] font-mono uppercase tracking-wider border border-amber-400/60 text-amber-300 hover:bg-amber-500/10"
              >
                PDF
              </button>
            </div>
          </div>
        ))
      )}
    </section>
  );
}

function SaveViewDrawer({ saveId, onClose, onDelete }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["save", saveId],
    queryFn: () => api.get(`/saves/${saveId}`).then((r) => r.data),
    retry: false,
  });
  const isLocked = error?.response?.status === 423;
  return (
    <div
      data-testid="save-view-drawer"
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex justify-end"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl h-full overflow-y-auto bg-[#0A0A0A] border-l border-amber-400/40 p-5"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-amber-300">
              SAVE_VIEW · {data?.layer || "…"}
            </div>
            <h2 className="font-mono text-xl mt-1 text-white truncate">
              {data?.title || "Loading…"}
            </h2>
            <div className="text-[10px] font-mono text-zinc-500 mt-1">
              {data?.created_at ? fmtDate(data.created_at) : ""}
            </div>
          </div>
          <button
            data-testid="save-view-close"
            onClick={onClose}
            className="h-8 px-3 border border-[#27272A] text-zinc-300 hover:text-white font-mono uppercase text-[10px] tracking-wider"
          >
            CLOSE
          </button>
        </div>
        <div className="mt-4 border-t border-[#161616] pt-4">
          {isLoading ? (
            <div className="text-[12px] font-mono text-zinc-500">LOADING…</div>
          ) : isLocked ? (
            <div className="border border-amber-400/60 bg-amber-500/5 p-4">
              <div className="text-[12px] font-mono uppercase tracking-wider text-amber-300">
                PIN_LOCKED · UNLOCK_VIA_/security
              </div>
              <p className="text-[11px] font-mono text-zinc-400 mt-2">
                This save is PIN-locked. Enter your PIN at{" "}
                <a href="/security" className="underline hover:text-white">/security</a>{" "}
                (60-second unlock window) then re-open this item.
              </p>
            </div>
          ) : (
            <>
              {data?.text && (
                <pre className="text-[13px] font-mono text-zinc-100 whitespace-pre-wrap leading-relaxed">
                  {data.text}
                </pre>
              )}
              {data?.note && (
                <div className="mt-4 text-[11px] font-mono text-amber-300 border-l-2 border-amber-400/60 pl-3">
                  ↳ {data.note}
                </div>
              )}
              {data?.tags?.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-1">
                  {data.tags.map((t) => (
                    <span key={t} className="px-2 py-0.5 border border-[#27272A] text-[10px] font-mono text-zinc-400">
                      #{t}
                    </span>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
        <div className="mt-6 border-t border-[#161616] pt-4 flex items-center justify-between gap-2">
          <button
            data-testid="save-view-delete"
            onClick={() => {
              if (window.confirm("Delete this save permanently?")) onDelete?.();
            }}
            className="h-9 px-3 border border-rose-400/60 text-rose-300 hover:bg-rose-500/10 font-mono uppercase text-[10px] tracking-wider inline-flex items-center gap-1"
          >
            <Trash2 size={11} /> DELETE
          </button>
          <a
            href="/bot"
            data-testid="save-view-remix"
            className="h-9 px-3 border border-purple-400/60 text-purple-200 hover:bg-purple-500/10 font-mono uppercase text-[10px] tracking-wider inline-flex items-center"
          >
            REMIX_TO_INNOVATION →
          </a>
        </div>
      </div>
    </div>
  );
}
